import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  NavLink,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  FiBarChart2,
  FiCalendar,
  FiClock,
  FiGift,
  FiLogOut,
  FiMenu,
  FiMessageSquare,
  FiRefreshCw,
  FiSettings,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import { useAuth } from '../auth/AuthContext';
import './Navbar.css';
import './ChatLocationPulse.css';
import { useAeonasUnread } from './AeonasUnread';
import './BadgeLayout.css';
// prime-ink-badge-layout-v1
import ConsultationActionBadges from './ConsultationActionBadges';
import {EMPTY_ACTION_COUNTS, actionSummary, actionLabel, actionTarget} from '../hooks/useConsultationActionCounts';

function Navbar({
  actionCounts = EMPTY_ACTION_COUNTS,
  locations = [],
  selectedLocationId = '',
  setSelectedLocationId = () => { },
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const consultationActions = actionSummary(actionCounts);
  const [voucherCount, setVoucherCount] =
    useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] =
    useState(false);

  const location = useLocation();
  const chatUnread = useAeonasUnread();
  const previousChatActivity = useRef(null);
  const pulseTimer = useRef(null);
  const [chatPulses,setChatPulses] = useState([]);
  useEffect(() => {
    if(!chatUnread.loaded)return;
    const previous=previousChatActivity.current;
    previousChatActivity.current=chatUnread.incoming;
    if(!previous)return;
    const studios=new Set();
    for(const [key,id] of Object.entries(chatUnread.incoming||{})) {
      if(Number(id)<=Number(previous[key]||0))continue;
      if(key.startsWith('studio:'))studios.add(Number(key.slice(7)));
      else if(key.startsWith('dm:')) {
        const studio=Number(chatUnread.dmLocations[key]);
        if([1,2,3].includes(studio))studios.add(studio);else [1,2,3].forEach(id=>studios.add(id));
      } else [1,2,3].forEach(studio=>studios.add(studio));
    }
    if(studios.size){clearTimeout(pulseTimer.current);setChatPulses([...studios]);pulseTimer.current=setTimeout(()=>setChatPulses([]),1800);}
  },[chatUnread.loaded,chatUnread.incoming,chatUnread.dmLocations]);
  useEffect(()=>()=>clearTimeout(pulseTimer.current),[]);

  useEffect(() => {
  const fetchCounts = async () => {
    try {
      const voucherResponse = await axios.get('/api/vouchers/unseen-count');

      setVoucherCount(
        Number(voucherResponse.data.count) || 0
      );
    } catch (err) {
      console.error(
        'Navbar Counts konnten nicht geladen werden:',
        err
      );
    }
  };

  fetchCounts();

  const interval = setInterval(
    fetchCounts,
    50000
  );

  return () => clearInterval(interval);
}, []);

  // Menü nach Navigation automatisch schließen
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Escape-Taste und Scroll-Sperre
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener(
        'keydown',
        handleEscape
      );

      document.body.classList.add(
        'mobile-menu-open'
      );
    }

    return () => {
      document.removeEventListener(
        'keydown',
        handleEscape
      );

      document.body.classList.remove(
        'mobile-menu-open'
      );
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout fehlgeschlagen:', err);
    } finally {
      setMobileMenuOpen(false);
      navigate('/login', { replace: true });
    }
  };

  const navItems = [
    {
      to: '/',
      label: 'Bookings',
      icon: FiCalendar,
      end: true,
    },
    {
      to: '/chat',
      label: 'Chat',
      icon: FiMessageSquare,
      badge: chatUnread.total,
    },
    {
      to: '/guest-spots',
      label: 'Guest Spots',
      icon: FiUsers,
    },
    {
      to: '/dayplan',
      label: 'DayPlan',
      icon: FiClock,
    },
    {
      to: actionTarget(actionCounts),
      consultationActions: true,
      label: 'Consultations',
      icon: FiMessageSquare,
      badge: consultationActions.total,
      changeCount: consultationActions.changeCount,
    },
    {
      to: '/rebooking',
      label: 'Rebooking',
      icon: FiRefreshCw,
    },
    {
      to: '/vouchers',
      label: 'Gutscheine',
      icon: FiGift,
      badge: voucherCount,
    },
    {
      to: '/insights',
      label: 'Insights',
      icon: FiBarChart2,
    },
    {
      to: '/admin',
      label: 'Settings',
      icon: FiSettings,
    },
  ];

  const visibleNavItems =
    user?.role === 'manager'
      ? navItems.filter(
        (item) =>
          item.to !== '/insights' &&
          item.to !== '/admin'
      )
      : navItems;

  return (
    <>
      <nav className="navbar fixed">
        {/* Desktop Navigation */}
        <div className="navbar-links">
          {visibleNavItems.map((item) => item.consultationActions ? (
            <div key="consultation-actions" className="pi-nav-consultation">
              <NavLink to={item.to} className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>{item.label}</NavLink>
              <ConsultationActionBadges count={consultationActions} counts={actionCounts} />
            </div>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              aria-label={item.consultationActions ? actionLabel('Beratungen', consultationActions) : undefined}
              className={({ isActive }) =>
                isActive
                  ? 'nav-link active'
                  : 'nav-link'
              }
            >
              {item.label}

              {item.badge > 0 && (
                <span className={`consultation-badge ${item.changeCount > 0 ? 'action-change-badge' : ''}`}>
                  {item.badge > 99
                    ? '99+'
                    : item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </div>

        <button
          type="button"
          className="desktop-logout-button"
          onClick={handleLogout}
          title="Abmelden"
        >
          <FiLogOut />
          <span>Logout</span>
        </button>

        {/* Standortwahl + mobiler Hamburger */}
        <div className="navbar-mobile-row">
          <div className="navbar-location" data-chat-alert={chatPulses.includes(Number(selectedLocationId))}>
            <div className="mobile-location-buttons">
              <button
                type="button"
                data-chat-alert={chatPulses.includes(3)}
                className={
                  Number(selectedLocationId) === 3
                    ? 'active'
                    : ''
                }
                onClick={() =>
                  setSelectedLocationId(3)
                }
              >
                Dortmund
              </button>

              <button
                type="button"
                data-chat-alert={chatPulses.includes(2)}
                className={
                  Number(selectedLocationId) === 2
                    ? 'active'
                    : ''
                }
                onClick={() =>
                  setSelectedLocationId(2)
                }
              >
                Münster
              </button>

              <button
                type="button"
                data-chat-alert={chatPulses.includes(1)}
                className={
                  Number(selectedLocationId) === 1
                    ? 'active'
                    : ''
                }
                onClick={() =>
                  setSelectedLocationId(1)
                }
              >
                Essen
              </button>
            </div>

            <select
              value={selectedLocationId}
              onChange={(event) =>
                setSelectedLocationId(
                  event.target.value
                )
              }
              aria-label="Standort auswählen"
            >
              {locations.map((locationItem) => (
                <option
                  key={locationItem.id}
                  value={locationItem.id}
                >
                  {locationItem.name}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className={`mobile-menu-button ${mobileMenuOpen ? 'open' : ''
              }`}
            onClick={() =>
              setMobileMenuOpen(
                (current) => !current
              )
            }
            aria-label={
              mobileMenuOpen
                ? 'Navigation schließen'
                : 'Navigation öffnen'
            }
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            {mobileMenuOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
      </nav>

      {/* Abdunklung */}
      <button
        type="button"
        className={`mobile-menu-overlay ${mobileMenuOpen ? 'visible' : ''
          }`}
        onClick={closeMobileMenu}
        aria-label="Navigation schließen"
        tabIndex={mobileMenuOpen ? 0 : -1}
      />

      {/* Mobiles Slide-in-Menü */}
      <aside
        id="mobile-navigation"
        className={`mobile-menu-drawer ${mobileMenuOpen ? 'open' : ''
          }`}
        aria-hidden={!mobileMenuOpen}
      >
        <div className="mobile-menu-header">
          <div>
            <span className="mobile-menu-eyebrow">
              Aeonas
            </span>
            <h2>Navigation</h2>
          </div>

          <button
            type="button"
            className="mobile-menu-close"
            onClick={closeMobileMenu}
            aria-label="Navigation schließen"
          >
            <FiX />
          </button>
        </div>

        <div className="mobile-menu-links">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            if (item.consultationActions) return (
              <div key="consultation-actions" className="pi-mobile-consultation">
                <NavLink to={item.to} onClick={closeMobileMenu} className={({isActive}) => isActive ? 'mobile-menu-link active' : 'mobile-menu-link'}>
                  <span className="mobile-menu-link-icon"><Icon /></span><span className="mobile-menu-link-label">{item.label}</span>
                </NavLink>
                <ConsultationActionBadges count={consultationActions} counts={actionCounts} onClick={closeMobileMenu} />
              </div>
            );

            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={closeMobileMenu}
                aria-label={item.consultationActions ? actionLabel('Beratungen', consultationActions) : undefined}
                className={({ isActive }) =>
                  isActive
                    ? 'mobile-menu-link active'
                    : 'mobile-menu-link'
                }
              >
                <span className="mobile-menu-link-icon">
                  <Icon />
                </span>

                <span className="mobile-menu-link-label">
                  {item.label}
                </span>

                {item.badge > 0 && (
                  <span className={`mobile-menu-badge ${item.changeCount > 0 ? 'action-change-badge' : ''}`}>
                    {item.badge > 99
                      ? '99+'
                      : item.badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>

        <button
          type="button"
          className="mobile-logout-button"
          onClick={handleLogout}
        >
          <span className="mobile-menu-link-icon">
            <FiLogOut />
          </span>

          <span className="mobile-menu-link-label">
            Logout
          </span>
        </button>

      </aside>
    </>
  );
}

export default Navbar;
// prime-ink-cancellation-alerts-v1
